import { memberFromBack } from '@entities/Member/responseFormatters';
import type { MemberRequest } from '@entities/Member/schema';
import type { MemberBack, MemberFront } from '@entities/Member/types';
import { convertFiltersToApiParams } from '@features/ui-filters/api/filterUtils';
import { PaginatedOptions, PaginatedResponse } from '@root/src/types/common';
import { mapPaginatedResponse } from '@root/src/utils/formatters/responseAdapters';
import { fetchRequest, RequestMethod } from '@services/api/utils/utils';

const getMembers = async ({
  filters = {},
  page = 1,
  pageSize = 15,
  sort = { field: 'name', order: 'ASC' },
}: PaginatedOptions = {}): Promise<PaginatedResponse<MemberFront>> =>
  fetchRequest<PaginatedResponse<MemberBack>, PaginatedResponse<MemberFront>>({
    endpoint: '/members',
    method: RequestMethod.GET,
    query: convertFiltersToApiParams(filters, {
      page,
      pageSize,
      sorted: [
        {
          id: sort.field,
          desc: sort.order,
        },
      ],
    }),
    responseFormatter: data => mapPaginatedResponse(data, memberFromBack),
  });

const getMember = async (id: string): Promise<MemberFront> =>
  fetchRequest<MemberBack, MemberFront>({
    endpoint: `/members/${id}`,
    method: RequestMethod.GET,
    responseFormatter: memberFromBack,
  });

const createMember = async (data: MemberRequest): Promise<MemberFront> =>
  fetchRequest<MemberBack, MemberFront, MemberRequest>({
    endpoint: `/members`,
    method: RequestMethod.POST,
    body: data,
    responseFormatter: memberFromBack,
  });

const updateMember = async (data: MemberRequest, id: string): Promise<MemberFront> =>
  fetchRequest<MemberBack, MemberFront, MemberRequest>({
    endpoint: `/members/${id}`,
    method: RequestMethod.PUT,
    body: data,
    responseFormatter: memberFromBack,
  });

const deleteMember = async (id: string): Promise<void> =>
  fetchRequest<{ id: string }, void, { id: string }>({
    endpoint: `/members/${id}`,
    method: RequestMethod.DELETE,
  });

export const membersApi = {
  getMembers,
  getMember,
  createMember,
  updateMember,
  deleteMember,
};
